/**
 * Portal end-user account page (ADR-0017) — slug-less, tenant-branded, reached
 * from the in-app avatar popover. Self-service: profile (name / email), password
 * change, and 2FA (enable / disable, gated by the instance policy). Mirrors the
 * launcher chrome so the global account surface feels part of the portal.
 *
 * Requires a portal session — anonymous visitors bounce to the parent login.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

import { TwoFactorPanel } from "@/components/auth/totp";
import { ApiError } from "@/lib/api";
import { portalApi } from "@/lib/api-portal";
import { portalTotpAdapter } from "@/lib/api-2fa";
import {
  parentAccountPath,
  parentLauncherPath,
  parentLoginPath,
  usePortalAuth,
} from "@/lib/portalAuth";

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

const inputCls =
  "h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground " +
  "outline-none transition placeholder:text-muted-foreground/70 focus:border-foreground/40 " +
  "focus:ring-2 focus:ring-foreground/10";

function ProfileCard() {
  const { t } = useTranslation();
  const setSession = usePortalAuth((s) => s.setSession);
  const token = usePortalAuth((s) => s.token);
  const user = usePortalAuth((s) => s.user);
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => portalApi.updateProfile({ full_name: fullName.trim() || null, email: email.trim() }),
    onSuccess: (u) => {
      setErr(null);
      if (token) setSession(token, u);
    },
    onError: (e) =>
      setErr(e instanceof ApiError && e.code.includes("account_exists")
        ? t("portal.emailTaken", "Questa email è già in uso.")
        : t("portalAccount.saved")),
  });

  return (
    <Card title={t("portalAccount.profile")}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
        className="max-w-md space-y-3"
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            {t("portalAccount.fullName")}
          </span>
          <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            {t("portalAccount.email")}
          </span>
          <input
            type="email"
            required
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {err && <p className="text-xs text-danger">{err}</p>}
        {save.isSuccess && <p className="text-xs text-success">{t("portalAccount.saved")}</p>}
        <button
          type="submit"
          disabled={save.isPending}
          className="flex h-10 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60"
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("portalAccount.save")}
        </button>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => portalApi.changePassword({ current_password: cur, new_password: next }),
    onSuccess: () => {
      setErr(null);
      setCur("");
      setNext("");
      setConfirm("");
    },
    onError: () => setErr(t("twoFactor.invalidCode")),
  });

  const valid = cur.length > 0 && next.length >= 8 && next === confirm && next !== cur;

  return (
    <Card title={t("portalAccount.changePassword")}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) change.mutate();
        }}
        className="max-w-md space-y-3"
      >
        {(["cur", "next", "confirm"] as const).map((k) => (
          <label key={k} className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              {k === "cur"
                ? t("portalAccount.currentPassword")
                : k === "next"
                  ? t("portalAccount.newPassword")
                  : t("portalAccount.confirmPassword")}
            </span>
            <input
              type="password"
              autoComplete={k === "cur" ? "current-password" : "new-password"}
              className={inputCls}
              value={k === "cur" ? cur : k === "next" ? next : confirm}
              onChange={(e) =>
                (k === "cur" ? setCur : k === "next" ? setNext : setConfirm)(e.target.value)
              }
            />
          </label>
        ))}
        {confirm.length > 0 && next !== confirm && (
          <p className="text-xs text-danger">{t("portalAccount.passwordMismatch")}</p>
        )}
        {err && <p className="text-xs text-danger">{err}</p>}
        {change.isSuccess && <p className="text-xs text-success">{t("portalAccount.passwordChanged")}</p>}
        <button
          type="submit"
          disabled={!valid || change.isPending}
          className="flex h-10 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60"
        >
          {change.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("portalAccount.changePassword")}
        </button>
      </form>
    </Card>
  );
}

/**
 * Per-app profile card — the member's EDITABLE attributes in one app (the
 * User-Profile self-service surface). Locked (operator-only / RLS-bearing)
 * fields are never returned by the API, so they can't appear here. Renders
 * nothing when the app declares no editable fields.
 */
function AppProfileCard({ slug, name }: { slug: string; name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["portal-profile", slug],
    queryFn: () => portalApi.getMyProfile(slug),
  });
  const [vals, setVals] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!q.data) return;
    const init: Record<string, string> = {};
    for (const f of q.data.fields) {
      const v = q.data.values[f.key];
      init[f.key] = v === undefined || v === null ? "" : String(v);
    }
    setVals(init);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => portalApi.updateMyProfile(slug, vals),
    onSuccess: (p) => qc.setQueryData(["portal-profile", slug], p),
  });

  if (!q.data || q.data.fields.length === 0) return null;

  return (
    <Card title={name}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
        className="max-w-md space-y-3"
      >
        <p className="-mt-1 text-xs text-muted-foreground">
          {t("portalAccount.appProfileHint", "Your details in this app.")}
        </p>
        {q.data.fields.map((f) => (
          <label key={f.key} className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              {f.label}
            </span>
            {f.data_type === "select" ? (
              <select
                className={inputCls}
                value={vals[f.key] ?? ""}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              >
                <option value="">{t("portalAccount.appProfileNone", "— none —")}</option>
                {(f.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.data_type === "number" ? "number" : "text"}
                className={inputCls}
                value={vals[f.key] ?? ""}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              />
            )}
          </label>
        ))}
        {save.isSuccess && <p className="text-xs text-success">{t("portalAccount.saved")}</p>}
        <button
          type="submit"
          disabled={save.isPending}
          className="flex h-10 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60"
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("portalAccount.save")}
        </button>
      </form>
    </Card>
  );
}

/** Per-app personal API keys (ADR-0052 S4): a token that acts AS the member in
 *  THIS app, its data boundary the member's OWN role RLS. Self-serve mint/revoke;
 *  the plaintext is shown once. */
function AppApiKeysCard({ slug, name }: { slug: string; name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["portal-app-keys", slug],
    queryFn: () => portalApi.listAppKeys(slug),
  });
  const [newName, setNewName] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["portal-app-keys", slug] });

  const mint = useMutation({
    mutationFn: () => portalApi.createAppKey(slug, newName || undefined),
    onSuccess: (k) => {
      setCreated(k.value);
      setNewName("");
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (keyId: string) => portalApi.revokeAppKey(slug, keyId),
    onSuccess: invalidate,
  });

  const active = (q.data ?? []).filter((k) => !k.revoked_at);

  return (
    <Card title={t("portalAccount.apiKeysTitle", `Chiavi API · ${name}`)}>
      <div className="max-w-md space-y-3">
        <p className="-mt-1 text-xs text-muted-foreground">
          {t(
            "portalAccount.apiKeysHint",
            "Un token che agisce a tuo nome in questa app, con i tuoi permessi.",
          )}
        </p>
        {created && (
          <div className="rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs">
            <div className="mb-1 font-medium">
              {t("portalAccount.apiKeyOnce", "Copiala ora — non verrà più mostrata")}
            </div>
            <code className="block overflow-x-auto break-all rounded bg-background px-2 py-1 font-mono">
              {created}
            </code>
            <button
              type="button"
              onClick={() => setCreated(null)}
              className="mt-1 text-muted-foreground hover:text-foreground"
            >
              {t("common.close", "Chiudi")}
            </button>
          </div>
        )}
        {active.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("portalAccount.apiKeysNone", "Nessuna chiave attiva.")}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {active.map((k) => (
              <li key={k.key_id} className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="font-medium">{k.name || k.key_id.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => revoke.mutate(k.key_id)}
                  disabled={revoke.isPending}
                  className="text-danger hover:underline disabled:opacity-50"
                >
                  {t("portalAccount.apiKeyRevoke", "Revoca")}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <input
            className={inputCls}
            placeholder={t("portalAccount.apiKeyNamePh", "nome (opzionale)")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            type="button"
            onClick={() => mint.mutate()}
            disabled={mint.isPending}
            className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {mint.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("portalAccount.apiKeyCreate", "Genera")}
          </button>
        </div>
      </div>
    </Card>
  );
}

/** App-admin only (ADR-0052 S4b): provision app-scoped SERVICE ACCOUNTS for this
 *  app — service identities (not humans) with rotatable keys, confined to the app,
 *  permissions clamped to the apps surface. Shown only to a `can_admin`-role member. */
function AppServiceAccountsCard({ slug, name }: { slug: string; name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["portal-app-sa", slug],
    queryFn: () => portalApi.listAppServiceAccounts(slug),
  });
  // ADR-0053 S6: an app SA acts AS an app ROLE (its /svc boundary), not permissions.
  const rolesQ = useQuery({
    queryKey: ["portal-app-sa-roles", slug],
    queryFn: () => portalApi.listAppServiceAccountRoles(slug),
  });
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [openKeys, setOpenKeys] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["portal-app-sa", slug] });

  const create = useMutation({
    mutationFn: () =>
      portalApi.createAppServiceAccount(slug, { name: newName, role_id: newRole }),
    onSuccess: () => {
      setNewName("");
      setNewRole("");
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => portalApi.removeAppServiceAccount(slug, id),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: (sa: { id: string; enabled: boolean }) =>
      portalApi.updateAppServiceAccount(slug, sa.id, { enabled: !sa.enabled }),
    onSuccess: invalidate,
  });

  const accounts = q.data ?? [];

  return (
    <Card title={t("portalAccount.saTitle", `Service account · ${name}`)}>
      <div className="max-w-md space-y-3">
        <p className="-mt-1 text-xs text-muted-foreground">
          {t(
            "portalAccount.saHint",
            "Identità di servizio per le integrazioni di questa app (chiavi ruotabili).",
          )}
        </p>
        {accounts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("portalAccount.saNone", "Nessun service account.")}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {accounts.map((sa) => (
              <li key={sa.id} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {sa.name}
                    {sa.role_name && (
                      <span className="ml-2 font-normal text-muted-foreground">
                        · {sa.role_name}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => toggle.mutate(sa)}>
                      <span
                        className={sa.enabled ? "text-success" : "text-muted-foreground"}
                      >
                        {sa.enabled
                          ? t("common.enabled", "Abilitato")
                          : t("common.disabled", "Disabilitato")}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenKeys(openKeys === sa.id ? null : sa.id)}
                      className="text-foreground/70 hover:underline"
                    >
                      {t("portalAccount.saKeys", "Chiavi")}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove.mutate(sa.id)}
                      className="text-danger hover:underline"
                    >
                      {t("common.delete", "Elimina")}
                    </button>
                  </div>
                </div>
                {openKeys === sa.id && <SaKeys slug={slug} saId={sa.id} />}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <input
            className={inputCls}
            placeholder={t("portalAccount.saNamePh", "nome integrazione")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <select
            className={inputCls}
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            aria-label={t("portalAccount.saRole", "Ruolo")}
          >
            <option value="">{t("portalAccount.saPickRole", "— ruolo —")}</option>
            {(rolesQ.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending || !newName.trim() || !newRole}
            className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("portalAccount.saCreate", "Crea")}
          </button>
        </div>
      </div>
    </Card>
  );
}

/** Keys sub-panel for one service account (mint shows plaintext once / revoke). */
function SaKeys({ slug, saId }: { slug: string; saId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [created, setCreated] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["portal-app-sa", slug, saId, "keys"],
    queryFn: () => portalApi.listAppServiceAccountKeys(slug, saId),
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["portal-app-sa", slug, saId, "keys"] });
  const mint = useMutation({
    mutationFn: () => portalApi.mintAppServiceAccountKey(slug, saId),
    onSuccess: (k) => {
      setCreated(k.value);
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (keyId: string) => portalApi.revokeAppServiceAccountKey(slug, saId, keyId),
    onSuccess: invalidate,
  });
  const active = (q.data ?? []).filter((k) => !k.revoked_at);

  return (
    <div className="mt-2 space-y-1 rounded-md border border-border/60 bg-background/50 p-2">
      {created && (
        <code className="block overflow-x-auto break-all rounded bg-background px-2 py-1 font-mono text-[11px]">
          {created}
        </code>
      )}
      {active.map((k) => (
        <div key={k.key_id} className="flex items-center justify-between">
          <span className="font-mono">{k.name || k.key_id.slice(0, 8)}</span>
          <button
            type="button"
            onClick={() => revoke.mutate(k.key_id)}
            className="text-danger hover:underline"
          >
            {t("portalAccount.apiKeyRevoke", "Revoca")}
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => mint.mutate()}
        disabled={mint.isPending}
        className="text-foreground/70 hover:underline"
      >
        + {t("portalAccount.saMintKey", "Genera chiave")}
      </button>
    </div>
  );
}

/** One per-app profile card for each app the member belongs to (empty apps
 *  render nothing). Slug-less account page → iterate the launcher feed. */
function WorkspaceProfiles() {
  const token = usePortalAuth((s) => s.token);
  const appsQ = useQuery({
    queryKey: ["my-apps"],
    queryFn: () => portalApi.myApps(),
    enabled: !!token,
  });
  return (
    <>
      {(appsQ.data ?? []).map((a) => (
        <AppProfileCard key={a.slug} slug={a.slug} name={a.name} />
      ))}
      {(appsQ.data ?? []).map((a) => (
        <AppApiKeysCard key={`k-${a.slug}`} slug={a.slug} name={a.name} />
      ))}
      {(appsQ.data ?? [])
        .filter((a) => a.can_admin)
        .map((a) => (
          <AppServiceAccountsCard key={`sa-${a.slug}`} slug={a.slug} name={a.name} />
        ))}
    </>
  );
}

export function PortalAccountPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const token = usePortalAuth((s) => s.token);

  const brandQ = useQuery({
    queryKey: ["parent-brand"],
    queryFn: () => portalApi.getParentBrand(),
    staleTime: 5 * 60_000,
    enabled: !!token,
  });
  // Drop any stale 2FA status when leaving the page.
  useEffect(() => () => void qc.removeQueries({ queryKey: ["portal-2fa"] }), [qc]);

  if (!token) return <Navigate to={parentLoginPath(parentAccountPath())} replace />;

  const brand = brandQ.data;
  const tenantName = brand?.name || "Portal";
  const pageBg = brand?.theme.background || undefined;

  return (
    <div
      className="tray-light flex min-h-screen flex-col bg-background"
      style={pageBg ? { background: pageBg } : undefined}
    >
      <header className="flex items-center justify-between border-b border-border bg-surface px-6 py-3.5">
        <Link
          to={parentLauncherPath()}
          className="flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("portalAccount.back")}
        </Link>
        <span className="text-sm font-medium text-foreground">{tenantName}</span>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("portalAccount.title")}
          </h1>
          <ProfileCard />
          <WorkspaceProfiles />
          <PasswordCard />
          <Card title={t("portalAccount.security")}>
            <TwoFactorPanel adapter={portalTotpAdapter} />
          </Card>
        </div>
      </main>
    </div>
  );
}
